<script lang="ts">
  import { Dialog } from 'bits-ui';
  import XIcon from 'phosphor-svelte/lib/XIcon';

  import type { RecoveryStateView } from '#src/generated/protocol';
  import { i18n } from '#lib/i18n.js';
  import DialogFrame from '#lib/ui/primitives/DialogFrame.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import VerifyDeviceOptions from './VerifyDeviceOptions.svelte';

  interface Props {
    open?: boolean;
    recovery: RecoveryStateView;
    recoveryPassphrase?: boolean;
    onVerified?: () => void | Promise<void>;
    onReset: () => void;
  }

  let {
    open = $bindable(false),
    recovery,
    recoveryPassphrase = false,
    onVerified,
    onReset,
  }: Props = $props();

  function close(): void {
    open = false;
  }

  async function completeRecovery(): Promise<void> {
    close();
    await onVerified?.();
  }

  function resetIdentity(): void {
    close();
    onReset();
  }
</script>

<DialogFrame bind:open variant="verification">
  <div class="dialog-heading">
    <div>
      <Dialog.Title class="dialog-title">{i18n.t('settings.verifyThisDevice')}</Dialog.Title>
      <Dialog.Description class="dialog-description">
        {i18n.t('settings.chooseVerificationMethod')}
      </Dialog.Description>
    </div>
    <IconButton variant="ghost" size="small" label={i18n.t('common.close')} onclick={close}>
      <XIcon />
    </IconButton>
  </div>

  <VerifyDeviceOptions
    {recovery}
    {recoveryPassphrase}
    inputId="settings-recovery-key"
    onRequested={close}
    onRecovered={completeRecovery}
    onReset={resetIdentity}
  />
</DialogFrame>

<style>
  .dialog-heading {
    align-items: flex-start;
    display: flex;
    gap: var(--space-300);
    justify-content: space-between;
    margin-bottom: var(--space-400);
  }

  :global(.dialog-title),
  :global(.dialog-description) {
    margin: 0;
  }

  :global(.dialog-title) {
    font-size: var(--font-size-heading);
    font-weight: var(--font-weight-bold);
  }

  :global(.dialog-description) {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    margin-top: var(--space-200);
  }
</style>
